/**
 * Reports vocabulary. Response types come from schema.d.ts; the report *definition* (blocks,
 * scope, period) is a JSON document the server validates (server/nova/modules/reports/blocks.py)
 * and declares as `dict` — the editing model below mirrors it, defaults included.
 */
import type { components } from '@/lib/api/schema';
import { getDisplayTimeZone } from '@/lib/format';
import { msg } from '@/lib/i18n';
import { zonedMidnight } from '@/lib/timeRange';

type S = components['schemas'];
export type ReportOut = S['ReportOut'];
export type RunOut = S['RunOut'];
export type ShareOut = S['ShareOut'];
export type Snapshot = S['ReportSnapshot'];
export type SnapshotBlock = S['ReportBlock'];
export type BlockData = S['ReportBlockData'];
export type Template = S['ReportTemplateOut'];
export type Schedule = S['ReportScheduleOut'];
export type Recipient = S['ReportRecipient'];
export type TvBoard = S['TvLeaderboardOut'];
export type PublicReport = S['PublicReportOut'];

export type BlockType = 'kpi' | 'series' | 'ranking' | 'group_comparison' | 'table' | 'text' | 'ai_summary';

interface Base {
  title?: string | null;
}
export interface KpiBlock extends Base {
  type: 'kpi';
  metrics: string[];
  scores: string[];
  compare_previous: boolean;
}
export interface SeriesBlock extends Base {
  type: 'series';
  metric: string;
  granularity: 'day' | 'week' | 'month';
  entity: 'project' | 'vehicle' | 'driver';
  ids: string[];
}
export interface RankingBlock extends Base {
  type: 'ranking';
  entity: 'driver' | 'vehicle';
  sort: 'safety' | 'eco';
  n: number;
  order: 'best' | 'worst';
  min_km: number;
  cohort: string | null;
}
export interface GroupBlock extends Base {
  type: 'group_comparison';
  dimension: string;
  mode: 'membership' | 'current' | null;
}
export interface TableBlock extends Base {
  type: 'table';
  source: 'detections' | 'trips';
  types: string[];
  severity: ('low' | 'medium' | 'high')[];
  order: 'recent' | 'longest' | 'worst_safety';
  limit: number;
}
export interface TextBlock extends Base {
  type: 'text';
  body: string;
}
export interface AiBlock extends Base {
  type: 'ai_summary';
  focus: string;
}
export type BlockDef = KpiBlock | SeriesBlock | RankingBlock | GroupBlock | TableBlock | TextBlock | AiBlock;

/** Server defaults (blocks.py), so a new block is valid as soon as it is added. */
export function newBlock(type: BlockType): BlockDef {
  switch (type) {
    case 'kpi':
      return {
        type,
        title: null,
        metrics: ['km', 'driving_h', 'idle_pct', 'fuel_l_100km', 'co2_kg', 'speeding_pct'],
        scores: ['safety', 'eco'],
        compare_previous: true,
      };
    case 'series':
      return { type, title: null, metric: 'safety', granularity: 'day', entity: 'project', ids: [] };
    case 'ranking':
      return { type, title: null, entity: 'driver', sort: 'safety', n: 10, order: 'best', min_km: 20, cohort: null };
    case 'group_comparison':
      return { type, title: null, dimension: 'depot', mode: null };
    case 'table':
      return { type, title: null, source: 'detections', types: [], severity: [], order: 'recent', limit: 25 };
    case 'text':
      return { type, title: null, body: '' };
    case 'ai_summary':
      return { type, title: null, focus: '' };
  }
}

/** A stored block, completed with defaults (stored blocks are already normalised by the server). */
export function asBlock(raw: Record<string, unknown>): BlockDef {
  const type = (raw.type as BlockType) ?? 'text';
  return { ...newBlock(type), ...raw } as BlockDef;
}

export const BLOCK_LABELS: Record<BlockType, string> = {
  kpi: msg('Key figures'),
  series: msg('Over time'),
  ranking: msg('Ranking'),
  group_comparison: msg('Groups compared'),
  table: msg('Events or trips'),
  text: msg('Text'),
  ai_summary: msg('Written summary (AI)'),
};

export const BLOCK_HINTS: Record<BlockType, string> = {
  kpi: msg('Distance, hours, fuel, idling and scores against the previous period.'),
  series: msg('One figure or score per day, week or month on a real calendar axis.'),
  ranking: msg('Best or weakest drivers or vehicles by safety or eco.'),
  group_comparison: msg('Depots, teams or vehicle types side by side.'),
  table: msg('The period’s events or trips, newest, longest or worst first.'),
  text: msg('Your own words: context, instructions, a signature.'),
  ai_summary: msg('A paragraph written from the computed numbers; skipped when AI is off.'),
};

export type PeriodKind = 'last_7_days' | 'last_30_days' | 'last_week' | 'last_month' | 'month_to_date' | 'custom';
export interface Period {
  kind: PeriodKind;
  start?: string | null;
  end?: string | null;
}
export interface Scope {
  project_ids: string[];
  group_id: string | null;
}

export const PERIOD_LABELS: Record<PeriodKind, string> = {
  last_7_days: msg('Last 7 days'),
  last_30_days: msg('Last 30 days'),
  last_week: msg('Last week'),
  last_month: msg('Last month'),
  month_to_date: msg('This month so far'),
  custom: msg('Custom period'),
};

export function periodOf(raw: Record<string, unknown> | undefined): Period {
  const kind = (raw?.kind as PeriodKind) ?? 'last_7_days';
  return { kind: kind in PERIOD_LABELS ? kind : 'last_7_days', start: (raw?.start as string) ?? null, end: (raw?.end as string) ?? null };
}

export function scopeOf(raw: Record<string, unknown> | undefined): Scope {
  return { project_ids: Array.isArray(raw?.project_ids) ? (raw.project_ids as string[]) : [], group_id: (raw?.group_id as string) ?? null };
}

export const RUN_STATUS: Record<string, { label: string; tone: 'good' | 'medium' | 'bad' | 'info' | 'neutral' }> = {
  queued: { label: msg('Queued'), tone: 'neutral' },
  running: { label: msg('Generating'), tone: 'info' },
  done: { label: msg('Ready'), tone: 'good' },
  failed: { label: msg('Failed'), tone: 'bad' },
};

export const WEEKDAYS = [msg('Monday'), msg('Tuesday'), msg('Wednesday'), msg('Thursday'), msg('Friday'), msg('Saturday'), msg('Sunday')];

export const TV_PERIODS = ['last_7_days', 'last_30_days', 'month_to_date', 'last_month'] as const;
export type TvPeriod = (typeof TV_PERIODS)[number];

export interface TvConfig {
  title: string;
  entity: 'driver' | 'vehicle';
  sort: 'safety' | 'eco';
  period: TvPeriod;
  project_ids: string[];
  group_id: string | null;
  limit: number;
  min_km: number;
  page_seconds: number;
}

export function tvConfigOf(raw: Record<string, unknown> | undefined): Partial<TvConfig> {
  return (raw ?? {}) as Partial<TvConfig>;
}

/** Noon of a local calendar day (report timezone) as an instant, for the chart axis. */
export function dayInstant(isoDay: string, tz: string = getDisplayTimeZone()): number {
  const [y, m, d] = isoDay.slice(0, 10).split('-').map(Number);
  return zonedMidnight({ y: y ?? 1970, m: m ?? 1, d: d ?? 1 }, tz).getTime() + 12 * 3_600_000;
}

/** Is the share still usable? */
export function shareState(sh: ShareOut, now = Date.now()): 'active' | 'revoked' | 'expired' {
  if (sh.revoked_at) return 'revoked';
  return Date.parse(sh.expires_at) <= now ? 'expired' : 'active';
}
